import { Component, OnInit, ViewChild } from '@angular/core';
import { catchError, tap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { FeedbackSettings } from 'src/app/general-components/feedback/feedback-settings';
import { FeedbackType } from 'src/app/general-components/feedback/feedback-type';
import { FeedbackComponent } from 'src/app/general-components/feedback/feedback.component';
import { HttpErrorResponse } from '@angular/common/http';
import { ErrorResponse } from 'src/app/interfaces/bad-request';
import { concat, EMPTY, Observable } from 'rxjs';
import { SupplierService } from '../suppliers/supplier.service';
import { ReservedSpecialSku } from '../reserved-special-sku';
import { SupplierWithStats } from '../suppliers/supplier';
import { SpecialsService } from '../specials.service';
import { KrampSpecialsService } from './kramp-specials.service';
import { EmptyKrampSpecial, KrampSpecial } from './kramp-special';
import { NewKrampSpecialComponent } from './new-kramp-special/new-kramp-special.component';
import { KrampSpecialsTableComponent } from './kramp-specials-table/kramp-specials-table.component';

@Component({
  selector: 'app-kramp-specials',
  templateUrl: './kramp-specials.component.html',
  styleUrls: ['./kramp-specials.component.scss'],
})
export class KrampSpecialsComponent implements OnInit {
  @ViewChild(NewKrampSpecialComponent)
  newKrampSpecialComponent!: NewKrampSpecialComponent;
  @ViewChild(KrampSpecialsTableComponent)
  krampSpecialsTableComponent!: KrampSpecialsTableComponent;
  @ViewChild(FeedbackComponent)
  feedbackComponent!: FeedbackComponent;
  specials: KrampSpecial[] = [];
  newSpecial: KrampSpecial = EmptyKrampSpecial();
  failedSpecial: KrampSpecial | undefined;
  keepInputs: boolean = false;

  feedbackSettings: FeedbackSettings = {
    identifier: '',
    show: false,
    type: FeedbackType.error,
    message: 'Er is iets misgegaan sorry',
    closeButton: false,
  };
  newSpecialFeedbackIdentifier = 'newSpecialFeedback';
  generalFeedbackIdentifier = 'generalFeedback';

  reservedSpecialSku: ReservedSpecialSku | undefined;

  suppliers: SupplierWithStats[] = [];
  isLoading: boolean = false;

  constructor(
    private supplierService: SupplierService,
    private krampSpecialsService: KrampSpecialsService,
    private specialsService: SpecialsService,
    private router: Router
  ) {}

  ngOnInit(): void {
    // Sequential on purpose: Firefox aborts parallel requests to the VPN-only API hosts,
    // see "Parallel requests to private API hosts" in AGENTS.md.
    concat(this.getReservedSpecialSku(), this.getSuppliers(), this.getKrampSpecials()).subscribe();
  }

  getKrampSpecials(): Observable<unknown> {
    this.isLoading = true;
    return this.krampSpecialsService.getKrampSpecials().pipe(
      tap((specials: KrampSpecial[]) => {
        this.specials = specials;
        this.isLoading = false;
      }),
      catchError((error: Error) => {
        this.updateFeedback(
          this.generalFeedbackIdentifier,
          FeedbackType.error,
          true,
          'Er is iets foutgegaan bij het ophalen van de Kramp specials',
          'Terug naar hoofdmenu',
          false
        );
        this.isLoading = false;
        console.error(error);
        return EMPTY;
      })
    );
  }

  getSuppliers(): Observable<unknown> {
    this.isLoading = true;
    return this.supplierService.getSuppliers().pipe(
      tap((suppliers: SupplierWithStats[]) => {
        this.suppliers = suppliers.sort((supplierA, supplierB) => (supplierA.name > supplierB.name ? 1 : -1));
      }),
      catchError((error: Error) => {
        this.updateFeedback(
          this.generalFeedbackIdentifier,
          FeedbackType.error,
          true,
          'Er is iets fout gegaan bij het ophalen van de leveranciers',
          'Terug naar hoofdmenu',
          false
        );
        this.isLoading = false;
        console.error(error);
        return EMPTY;
      })
    );
  }

  getReservedSpecialSku(): Observable<unknown> {
    this.isLoading = true;
    return this.specialsService.getReservedSpecialSku().pipe(
      tap((reservedSpecialSku: ReservedSpecialSku) => {
        this.reservedSpecialSku = reservedSpecialSku;
      }),
      catchError((error: Error) => {
        this.updateFeedback(
          this.generalFeedbackIdentifier,
          FeedbackType.error,
          true,
          'Er kon geen special gereserveerd worden, probeer het later nog een keer',
          'Terug naar hoofdmenu',
          false
        );
        this.isLoading = false;
        console.error(error);
        return EMPTY;
      })
    );
  }

  setKeepInput(keepInput: boolean) {
    this.keepInputs = keepInput;
  }

  createSpecial(special: KrampSpecial) {
    this.isLoading = true;
    this.krampSpecialsService.createKrampSpecial(special).subscribe({
      next: (newSpecial: KrampSpecial) => {
        this.specials.push(newSpecial);

        this.krampSpecialsTableComponent.sortByField('creationDate', false);
        this.updateFeedback(
          this.newSpecialFeedbackIdentifier,
          FeedbackType.success,
          true,
          `De special ${newSpecial.sku} is aangemaakt!`,
          undefined,
          false
        );
        this.feedbackComponent.fadeOut(2000);
        this.getReservedSpecialSku().subscribe();
        if (!this.keepInputs) {
          this.newKrampSpecialComponent.clearSpecial();
        } else {
          this.newKrampSpecialComponent.clearUniqueFields();
        }
        this.isLoading = false;
      },
      error: (error: HttpErrorResponse) => {
        this.failedSpecial = special;
        console.error(error.error);
        if (error.status == 400) {
          const badRequestError = error.error as ErrorResponse;
          const errorException = badRequestError.exception;
          if (errorException?.includes('DoubleDescriptionException')) {
            const SKUs = this.getSKUsFromError(errorException);
            this.updateFeedback(
              this.newSpecialFeedbackIdentifier,

              FeedbackType.error,
              true,
              `Er bestaat al een special met de omschrijving "${special.description}": ${SKUs}`,
              'Aanpassen en opnieuw proberen',
              true
            );
          } else {
            this.updateFeedback(
              this.newSpecialFeedbackIdentifier,
              FeedbackType.error,
              true,
              'Er klopte *iets* niet aan de special',
              'Aanpassen en opnieuw proberen',
              true
            );
          }
        } else {
          this.updateFeedback(
            this.newSpecialFeedbackIdentifier,
            FeedbackType.error,
            true,
            'Er is iets fout gegaan bij aanmaken van de special',
            undefined,
            true
          );
        }

        this.isLoading = false;
      },
    });
  }

  getSKUsFromError(errorException: string) {
    const parts = errorException.match(/\[(.*?)\]/);
    return parts ? parts[1] : 'Niet beschikbaar';
  }

  retryFailedSpecial() {
    if (this.failedSpecial) {
      this.feedbackSettings.show = false;
      this.failedSpecial.creationDate = '';

      this.newKrampSpecialComponent.retryFailedSpecial(this.failedSpecial);
    }
  }

  updateFeedback(
    identifier: string,
    type: FeedbackType,
    show: boolean,
    message: string,
    buttonText: string | undefined,
    closeButton: boolean
  ) {
    this.feedbackSettings.identifier = identifier;
    this.feedbackSettings.type = type;
    this.feedbackSettings.show = show;
    this.feedbackSettings.message = message;
    this.feedbackSettings.buttonText = buttonText;
    this.feedbackSettings.closeButton = closeButton;
  }

  handleFeedbackOutput(identifier: string) {
    if (identifier == this.newSpecialFeedbackIdentifier) {
      this.retryFailedSpecial();
    } else {
      this.returnHome();
    }
  }

  returnHome() {
    void this.router.navigate(['home']);
  }

  feedbackType() {
    return FeedbackType;
  }
}
